import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CoachCard } from './CoachCard'
import { Dialog } from './Dialog'
import { HelpPanel } from './HelpPanel'
import { useShortcutRegistry } from './Shortcuts'
import { helpFor, type HelpEntry } from '../lib/help'
import { forgetTutorial, rememberTutorial } from '../lib/preferences'
import { advertisedHints } from '../lib/shortcuts'
import {
  resumeFor,
  withTutorial,
  withoutTutorial,
  type Tutorial,
  type TutorialId
} from '../lib/tutorials'

/**
 * Everything the shell knows about help, in one place the top bar, the keyboard and the card share.
 *
 * It used to live inside the question-mark button, and the `?` key reached it by finding that button
 * with `document.querySelector('.help-button')` and clicking it - which made a keyboard shortcut
 * depend on a class name in the stylesheet. There are now three ways in (the menu, the key, the
 * panel's own walkthrough chips) and one card that has to outlive every navigation, so the state is
 * held here and each of them asks for what it wants.
 *
 * Provided by `Shell`, around the whole frame, because the frame does not unmount on a navigation.
 * That is the property the card depends on: following a step's link changes the route and the card is
 * still there on the screen the step pointed at.
 */
interface HelpApi {
  /** The entry for the screen on view. */
  entry: HelpEntry
  /** A walkthrough with a place to go back to, whether or not its card is showing. */
  resume: { tutorial: Tutorial; step: number } | null
  openHelp: () => void
  openShortcuts: () => void
  start: (id: TutorialId) => void
  carryOn: () => void
}

const HelpContext = createContext<HelpApi | null>(null)

export function useHelp(): HelpApi {
  const api = useContext(HelpContext)
  if (!api) throw new Error('useHelp must be called inside HelpProvider.')
  return api
}

export function HelpProvider({ roles, children }: { roles: readonly string[]; children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()

  const [panelOpen, setPanelOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)

  /*
    Bumped whenever the stored place is written.

    `resumeFor` reads storage during render, and most writes arrive with a navigation that re-renders
    anyway. Putting the card down from a screen whose address carries no walkthrough does not
    navigate - there is nothing to clean - so without this the card would stay up until the next
    unrelated render.
  */
  const [, setRevision] = useState(0)
  const touch = () => setRevision((revision) => revision + 1)

  /*
    Asked for by the reader, so the card may take focus; a card that appears because a page loaded
    with a walkthrough in its address must not, or it would pull the caret out of whatever the operator
    was about to type into.
  */
  const [focusRequest, setFocusRequest] = useState(0)
  const requestFocus = () => setFocusRequest((request) => request + 1)

  const entry = helpFor(location.pathname, location.search, roles)
  const resume = resumeFor(location.search)
  const showing = resume && !resume.putDown ? resume : null

  /*
    A place a link carried is written to storage as soon as it is seen, and marked as not put down.

    Without this the two homes only agree for a reader who has pressed something (D-504): somebody
    arriving on `?help=binding&step=3` would have their place in the address and nowhere else, and the
    first rail link - a bare path - would lose it. Mirroring a value is idempotent and has no race
    with the router, which is why it is the one thing here allowed to be an effect on `location`.
  */
  useEffect(() => {
    if (!resume?.fromUrl) return
    rememberTutorial({ id: resume.tutorial.id, step: resume.step, putDown: false })
    // Primitives rather than the object, which is rebuilt on every render.
  }, [location.search, resume?.fromUrl, resume?.tutorial.id, resume?.step])

  function start(id: TutorialId) {
    navigate(`${location.pathname}${withTutorial(location.search, id, 1)}`, { replace: true })
    rememberTutorial({ id, step: 1, putDown: false })
    setPanelOpen(false)
    requestFocus()
    touch()
  }

  function carryOn() {
    if (!resume) return
    rememberTutorial({ id: resume.tutorial.id, step: resume.step, putDown: false })
    setPanelOpen(false)
    requestFocus()
    touch()
  }

  function step(tutorial: Tutorial, next: number) {
    navigate(`${location.pathname}${withTutorial(location.search, tutorial.id, next)}`, { replace: true })
    rememberTutorial({ id: tutorial.id, step: next, putDown: false })
    requestFocus()
  }

  /*
    Navigate, and leave the card where it is.

    This used to close the walkthrough as well, because it was a modal and a modal left open across a
    navigation makes the screen it arrives on unusable. The card is not modal, so the reader lands on
    the screen the step is about with the step still beside it - which is the whole point of reading
    along. The step rides in the destination's address too, so the link is shareable from there.
  */
  function follow(tutorial: Tutorial, to: string, from: number) {
    const [path, query] = to.split('?')
    rememberTutorial({ id: tutorial.id, step: from, putDown: false })
    navigate(`${path}${withTutorial(query ? `?${query}` : '', tutorial.id, from)}`)
  }

  /*
    Put down and finished are two acts, and the difference is what is remembered (D-498).

    Both take the card off the screen and both clean the address, so a link copied afterwards carries
    no walkthrough. Putting it down keeps the place and marks it, so the Help menu offers to carry on
    and the card does not follow the reader onto every screen they visit next. Only Done forgets.
  */
  function putDown(tutorial: Tutorial, at: number) {
    rememberTutorial({ id: tutorial.id, step: at, putDown: true })
    if (new URLSearchParams(location.search).has('help')) {
      navigate(`${location.pathname}${withoutTutorial(location.search)}`, { replace: true })
    }
    touch()
  }

  function finish() {
    forgetTutorial()
    if (new URLSearchParams(location.search).has('help')) {
      navigate(`${location.pathname}${withoutTutorial(location.search)}`, { replace: true })
    }
    touch()
  }

  const api: HelpApi = {
    entry,
    resume: resume ? { tutorial: resume.tutorial, step: resume.step } : null,
    openHelp: () => setPanelOpen(true),
    openShortcuts: () => setShortcutsOpen(true),
    start,
    carryOn
  }

  return (
    <HelpContext.Provider value={api}>
      {children}

      <Dialog open={panelOpen} title={entry.title} onClose={() => setPanelOpen(false)} data-testid="help-dialog">
        <HelpPanel entry={entry} onStartTutorial={start} />
      </Dialog>

      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {showing && (
        <CoachCard
          tutorial={showing.tutorial}
          step={showing.step}
          focusRequest={focusRequest}
          onStep={(next) => step(showing.tutorial, next)}
          onFollow={(to) => follow(showing.tutorial, to, showing.step)}
          onPutDown={() => putDown(showing.tutorial, showing.step)}
          onDone={finish}
        />
      )}
    </HelpContext.Provider>
  )
}

/**
 * The keys this console answers on the screen on view, at every width.
 *
 * The views pane already lists them, and it is hidden below 1120px - which is where a keyboard may be
 * the only thing somebody has, and the cost D-499 wrote down when `?` was added. The list here is the
 * same filtered list the pane draws, from the same function, so the two can never advertise
 * different keys.
 */
function ShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { handlers } = useShortcutRegistry()
  const hints = advertisedHints(handlers)

  return (
    <Dialog open={open} title="Keyboard shortcuts" onClose={onClose} data-testid="shortcuts-dialog">
      <dl className="shortcut-list">
        {hints.map((hint) => (
          <div key={hint.action}>
            <dt>{hint.what}</dt>
            <dd>
              <kbd>{hint.key}</kbd>
            </dd>
          </div>
        ))}
      </dl>
      <p className="hint">A key that only works on some screens is listed only where it works.</p>
    </Dialog>
  )
}
